/**
 * 15 - Artillery Battery (10 §2): 3-4 field guns in a line of sandbagged pits, muzzles to the south, three in
 * four of them is success. The guns are `hard` (LAW or bomb) and MANNED: each crew serves its piece as an
 * emplacement and fights, with a loader behind it. The ready ammunition is stacked in the pit, and the pit is
 * soft: a grenade in the pit cooks it off and the blast spikes the gun - but never the next pit along.
 */

import { PROPS } from '../../sim/props';
import { ENEMY } from '../../sim/tuning';
import { checker, clutter, guard, guardAtDoor, guardsAbout, insideRect, structure } from './helpers';
import { crew, dress, put } from './helpers3a';
import { checkTargets, cooksOff } from './helpers3aAsserts';
import type { ArchetypeSpec } from './types';

export const gunsFor = (threat: number): number => (threat >= 6 ? 4 : 3);

/** Pit to pit, centre to centre: the bags of one are 6.8 m from the heart of the next - no chain. */
const SPACING = 12;
/** The gun stands this far south of its pit's centre: inside the horseshoe, clear of its bags. */
const GUN_DZ = 0.4;

export const artilleryBattery: ArchetypeSpec = {
  id: 'artillery-battery',
  name: 'ARTILLERY BATTERY',
  nativeBiome: 'desert',
  baseThreat: 5,
  lengthTiles: 160,
  clockScale: 1,
  startAlert: 0,
  garrison: 'regular',
  perimeter: 'wire',
  sealed: false,
  layout: 'compound',
  size: { w: [50, 62], d: [34, 34] },
  sizeFor: (threat) => ({ w: gunsFor(threat) * SPACING + 14, d: 34 }),
  // North of the gun line: no muzzle may bear on the Osprey (validate/fairness.ts).
  pickupDz: [-10, -5],
  river: false,
  patrolLoop: 'inside',
  breach: { min: 0, max: 0 },

  build(g) {
    const count = gunsFor(g.threat);
    const cx = (g.rect.x0 + g.rect.x1) / 2;
    const z = Math.round((g.rect.z0 + g.rect.z1) / 2);
    const ids: number[] = [];
    for (let i = 0; i < count; i++) {
      const x = cx + (i - (count - 1) / 2) * SPACING;
      const pit = put(g, 'gunPit', x, z, { margin: 0.8 });
      if (pit.id < 0) continue;
      // The piece goes into its pit without asking: the horseshoe is drawn round exactly this much room.
      const gun = g.map.add('fieldGun', pit.x, pit.z + GUN_DZ, { target: true });
      ids.push(gun.id);
      crew(g, gun);
      // The loader, at the open back of the pit with the shells.
      guard(g, pit.x + 1, pit.z - 3.5, 0);
      dress(g, 'crate', pit.x - 1.5, pit.z - 3.5, { margin: 0.3 });
    }
    const post = structure(g, g.kit.shelter, { within: { ...g.inner, z1: z - 7 }, margin: 1.2 });
    structure(g, 'alarmPost', { margin: 1 });
    clutter(g, 'crate', 3);
    if (post) guardAtDoor(g, post);
    guardsAbout(g, g.rng.int(2));
    return {
      objective: {
        kind: 'destroyTargets', label: 'SPIKE THE GUNS', targetPropIds: ids, minFraction: 0.75,
        rules: [{ type: 'recall', mode: 'normal' }],
      },
    };
  },

  asserts(def) {
    const { check, out } = checker('artillery-battery');
    const guns = checkTargets(check, def, ['fieldGun']);
    const wanted = gunsFor(def.threat);
    check(guns.length === wanted, `needs ${wanted} guns at threat ${def.threat}, has ${guns.length}`);
    check(
      def.objective.kind === 'destroyTargets' && def.objective.minFraction === 0.75,
      'three guns in four is success',
    );
    check(
      PROPS.fieldGun.durability === 'hard' && PROPS.fieldGun.emplacement === true,
      'a field gun is a hard emplacement',
    );
    const pits = def.props.filter((p) => p.kind === 'gunPit');
    check(pits.length === guns.length, `${guns.length} guns need ${guns.length} pits, there are ${pits.length}`);
    for (const gun of guns) {
      check(insideRect(def.goalZone, gun.x, gun.z), `gun ${gun.id} stands outside the battery position`);
      const pit = pits.find((p) => p.x === gun.x && Math.abs(p.z + GUN_DZ - gun.z) < 0.01);
      check(pit !== undefined, `gun ${gun.id} does not stand in a pit`);
      // "The ammunition pits cook off": the pit's ready ammunition takes its own gun, and only its own.
      if (pit) check(cooksOff(pit, gun), `pit ${pit.id} would not take gun ${gun.id} with it`);
      check(pits.every((p) => p === pit || !cooksOff(p, gun)), `gun ${gun.id} is within reach of a neighbouring pit`);
      const crews = def.spawns.filter((s) => s.mans === gun.id);
      check(crews.length === 1 && crews[0]?.dormantUntil === undefined, `gun ${gun.id} must be manned from the start`);
      // Muzzles south: the crew's arc is the battery's front, and the way in is round the back.
      check(crews.every((s) => Math.abs(s.facing) < 0.01), `the crew of gun ${gun.id} must face south`);
    }
    // No piece may bear on the pickup (the fairness gate says so of every emplacement; here it is by design).
    check(guns.every((gun) => def.pickup.z < gun.z || Math.abs(def.pickup.x - gun.x) > ENEMY.emplacementRange),
      'the pickup must lie behind the gun line');
    return out;
  },
};
